import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Card,
  MessageBar,
  MessageBarBody,
  Spinner,
  Subtitle2,
  Tab,
  TabList,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Title2,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import type { RequestDetail } from '@lg/core';
import { QUESTIONS, SECTIONS, answerLines, hasAnyRole, type Question, type RequestStatus } from '@lg/shared';
import { useApi } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { PeopleWorkspace } from '../people/PeopleWorkspace';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { STATUS_COLOR, STATUS_LABEL, dateTime, errorMessage, saveFile, shortDate } from '../ui/format';
import { ApprovalsTab } from './ApprovalsTab';
import { DataPackageTab } from './DataPackageTab';
import { DeviationTable } from './DeviationTable';
import { ProvisioningTab } from './ProvisioningTab';
import { ReadinessTab } from './ReadinessTab';
import { ReportTab } from './ReportTab';

const useStyles = makeStyles({
  head: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
    marginBottom: tokens.spacingVerticalS,
  },
  spacer: { flex: 1 },
  meta: {
    display: 'flex',
    gap: tokens.spacingHorizontalXL,
    flexWrap: 'wrap',
    marginBottom: tokens.spacingVerticalM,
    color: tokens.colorNeutralForeground2,
  },
  tabs: { marginBottom: tokens.spacingVerticalL },
  card: {
    padding: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  stack: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  label: { width: '40%', color: tokens.colorNeutralForeground2 },
  deviation: { backgroundColor: tokens.colorPaletteYellowBackground1 },
});

type TabId =
  'answers' | 'people' | 'approvals' | 'provisioning' | 'report' | 'readiness' | 'package' | 'audit';

const DEFAULT_TAB: Record<RequestStatus, TabId> = {
  Draft: 'answers',
  Submitted: 'approvals',
  InReview: 'approvals',
  Approved: 'approvals',
  Provisioning: 'provisioning',
  NeedsAttention: 'provisioning',
  Validating: 'report',
  Ready: 'report',
  Cancelled: 'answers',
};

export function RequestDetailPage() {
  const { id = '' } = useParams();
  const styles = useStyles();
  const api = useApi();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [tab, setTab] = useState<TabId | null>(null);
  const detail = useQuery({ queryKey: ['request', id], queryFn: () => api.getRequest(id) });

  if (detail.isPending) return <Spinner label="Loading request…" />;
  if (detail.isError)
    return (
      <MessageBar intent="error">
        <MessageBarBody>{errorMessage(detail.error)}</MessageBarBody>
      </MessageBar>
    );
  const d = detail.data;
  const r = d.request;
  // Open on the tab that matters for the request's status.
  const current: TabId = tab ?? DEFAULT_TAB[r.status];
  const canCancel =
    ['Draft', 'Submitted', 'InReview', 'Approved'].includes(r.status) &&
    ((user.upn === r.requesterUpn && hasAnyRole(user, ['Requester'])) || hasAnyRole(user, ['D365Admin']));

  return (
    <div>
      <div className={styles.head}>
        <Title2 as="h1">{r.venueName}</Title2>
        <Badge appearance="tint" color={STATUS_COLOR[r.status]}>
          {STATUS_LABEL[r.status]}
        </Badge>
        <span className={styles.spacer} />
        {canCancel && (
          <ConfirmDialog
            trigger={<Button appearance="subtle">Cancel request</Button>}
            title="Cancel this request"
            confirmLabel="Cancel request"
            onConfirm={async () => {
              qc.setQueryData(['request', id], await api.cancelRequest(id));
              void qc.invalidateQueries({ queryKey: ['requests'] });
            }}
          >
            <Body1>
              The request is closed and its legal entity ID is released. Nothing has been created in D365 yet.
            </Body1>
          </ConfirmDialog>
        )}
        {d.canEdit && (
          <Button
            appearance={r.status === 'Draft' ? 'primary' : 'secondary'}
            onClick={() => navigate(`/requests/${id}/edit/venue`)}
          >
            {r.status === 'Draft' ? 'Continue intake' : 'Edit (returns to Draft)'}
          </Button>
        )}
      </div>
      <div className={styles.meta}>
        <Body1>
          Legal entity <b>{r.legalEntityId ?? '—'}</b>
        </Body1>
        <Body1>
          Template <b>{r.templateId}</b>
        </Body1>
        <Body1>
          Gate <b>{r.currentGate}</b>
        </Body1>
        <Body1>
          Go-live <b>{shortDate(r.targetGoLive)}</b>
        </Body1>
        <Body1>
          Requester <b>{r.requesterUpn}</b>
        </Body1>
        <Body1>
          Version <b>{r.version}</b>
        </Body1>
      </div>
      <TabList
        className={styles.tabs}
        selectedValue={current}
        onTabSelect={(_, t) => setTab(t.value as TabId)}
      >
        <Tab value="answers">
          Answers
          {d.deviations.length
            ? ` (${d.deviations.length} deviation${d.deviations.length > 1 ? 's' : ''})`
            : ''}
        </Tab>
        <Tab value="people">People and approvals</Tab>
        <Tab value="approvals">Approvals</Tab>
        <Tab value="provisioning">Provisioning</Tab>
        <Tab value="report">Report and tasks</Tab>
        <Tab value="readiness">Readiness</Tab>
        <Tab value="package">D365 data package</Tab>
        <Tab value="audit">Audit</Tab>
      </TabList>
      {current === 'answers' && <AnswersTab detail={d} />}
      {current === 'people' && (
        <PeopleWorkspace requestId={id} legalEntityId={r.legalEntityId} editable={d.canEdit} />
      )}
      {current === 'approvals' && <ApprovalsTab detail={d} onStarted={() => setTab('provisioning')} />}
      {current === 'provisioning' && <ProvisioningTab requestId={id} />}
      {current === 'report' && <ReportTab detail={d} />}
      {current === 'readiness' && <ReadinessTab requestId={id} />}
      {current === 'package' && <DataPackageTab requestId={id} />}
      {current === 'audit' && <AuditTab requestId={id} />}
    </div>
  );
}

/** One line per value: overlays one per row, lists joined, objects (address) joined. */
function Display({ question, value }: { question: Question; value: unknown }) {
  const lines = answerLines(question, value);
  if (lines.length === 0)
    return (
      <>
        {question.type === 'journals' || question.type === 'accountOverrides' ? 'None (template as is)' : '—'}
      </>
    );
  if (lines.length === 1) return <>{lines[0]}</>;
  return (
    <>
      {lines.map((l) => (
        <div key={l}>{l}</div>
      ))}
    </>
  );
}

function AnswersTab({ detail }: { detail: RequestDetail }) {
  const styles = useStyles();
  const api = useApi();
  const download = useMutation({
    mutationFn: async () => saveFile(await api.requestFile(detail.request.id)),
  });
  return (
    <div className={styles.stack}>
      {detail.deviationDetails.length > 0 && (
        <Card className={styles.card}>
          <MessageBar intent="warning" layout="multiline">
            <MessageBarBody>
              Highlighted answers differ from the template defaults. Ledger overlays apply to{' '}
              {detail.request.legalEntityId ?? 'this venue'} only.
            </MessageBarBody>
          </MessageBar>
          <DeviationTable details={detail.deviationDetails} />
        </Card>
      )}
      <div className={styles.head}>
        <Caption1>
          Reuse these settings and overlays for the next venue: the file leaves the venue&apos;s name, legal
          entity, address, EIN and people blank.
        </Caption1>
        <span className={styles.spacer} />
        <Button disabled={download.isPending} onClick={() => download.mutate()}>
          Download as request file
        </Button>
      </div>
      {download.isError && <MessageBar intent="error">{errorMessage(download.error)}</MessageBar>}
      {SECTIONS.filter((s) => s.id !== 'people').map((s) => {
        const qs = QUESTIONS.filter((q) => q.section === s.id && detail.answers[q.key] !== undefined);
        if (!qs.length) return null;
        return (
          <Card key={s.id} className={styles.card}>
            <Subtitle2 as="h2">{s.title}</Subtitle2>
            <Table size="small">
              <TableBody>
                {qs.map((q) => {
                  const deviates = detail.deviations.includes(q.key);
                  const errs = detail.errors[q.key];
                  return (
                    <TableRow key={q.key} className={deviates ? styles.deviation : undefined}>
                      <TableCell className={styles.label}>{q.label}</TableCell>
                      <TableCell>
                        <Display question={q} value={detail.answers[q.key]} />
                        {deviates && (
                          <>
                            {' '}
                            <Badge size="small" color="warning">
                              differs from template
                            </Badge>
                          </>
                        )}
                        {errs && (
                          <>
                            {' '}
                            <Badge size="small" color="danger">
                              {errs.join('; ')}
                            </Badge>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        );
      })}
    </div>
  );
}

function AuditTab({ requestId }: { requestId: string }) {
  const styles = useStyles();
  const api = useApi();
  const audit = useQuery({ queryKey: ['audit', requestId], queryFn: () => api.listAudit(requestId) });
  if (audit.isPending) return <Spinner />;
  return (
    <Card className={styles.card}>
      <Table size="small">
        <TableBody>
          {(audit.data ?? []).map((e) => (
            <TableRow key={e.id}>
              <TableCell style={{ width: 160 }}>{dateTime(e.at)}</TableCell>
              <TableCell style={{ width: 260 }}>{e.actorUpn}</TableCell>
              <TableCell>
                <b>{e.action}</b> <Caption1>{e.payload ? JSON.stringify(e.payload) : ''}</Caption1>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}
